import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const HIDE_DELAY_MS = 3000;

type ArgT = {
  /** Keep the controls pinned visible (e.g. while a flyout is open). */
  hold: boolean;

  paused: boolean;
  throbber: boolean;
};

type ResultT = {
  /** Show the controls and (re)arm the inactivity timer. */
  reveal: () => void;

  /** Whether the controls overlay should currently be visible. */
  visible: boolean;
};

/**
 * Auto-hides the player controls after a period of inactivity while playing.
 * Controls always stay visible while paused or loading (throbber). `reveal()`
 * shows them and restarts the hide timer; call it on pointer/keyboard activity.
 */
export function useAutoHide({ hold, paused, throbber }: ArgT): ResultT {
  // `hidden` only tracks the user-inactivity auto-hide; the paused/throbber/hold
  // "always visible" rules are folded into `visible` below so the effect never
  // has to call setState synchronously.
  const [hidden, setHidden] = useState(false);
  const timerRef = useRef<null | number>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearTimer();
    if (paused || throbber || hold) return;
    timerRef.current = window.setTimeout(() => {
      setHidden(true);
    }, HIDE_DELAY_MS);
  }, [clearTimer, hold, paused, throbber]);

  const reveal = useCallback(() => {
    setHidden(false);
    scheduleHide();
  }, [scheduleHide]);

  // (Re)arm the timer whenever the inputs change; the cleanup also clears it on
  // unmount, so it can't fire after navigating away (e.g. on Finish).
  useEffect(() => {
    scheduleHide();
    return clearTimer;
  }, [scheduleHide, clearTimer]);

  return { reveal, visible: paused || throbber || hold || !hidden };
}
